import { AntDesign } from "@expo/vector-icons";
import { router, useFocusEffect } from "expo-router";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { View, ScrollView, Pressable } from "react-native";
import { useDispatch } from "react-redux";

import Logo from "@/assets/logo.svg";
import Copyright from "@/components/Copyright";
import Loader from "@/components/Loader";
import Subtitle from "@/components/Subtitle";
import Title from "@/components/Title";
import Button from "@/components/button/Button";
import ButtonLogout from "@/components/button/ButtonLogout";
import IndividualCard from "@/components/card/CardIndividual";
import IndividualModal from "@/components/modal/onboarding/IndividualModal";
import { setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import { storage } from "@/context/storage";
import { sendRequest } from "@/utils/ApiUtil";
import { toastInstance } from "@/utils/ToastConfig";

export default function AddIndividual() {
  const [render, setRender] = useState(false);
  const [loading, setLoading] = useState(false);
  const [individuals, setIndividuals] = useState<any>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [blocked, setBlocked] = useState(true);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const globalValues = useAppSelector((state: any) => state);

  const { handleSubmit } = useForm<FormData>({
    mode: "onSubmit",
  });

  useFocusEffect(
    React.useCallback(() => {
      const request = fetchData();
      if (request) {
        if (globalValues.isIndividualReviewFinished) {
          toastInstance(
            "success",
            t("common:toast.subtitle.individualupdated")
          );
        } else if (globalValues.addIndividualAdded) {
          toastInstance("success", t("common:toast.subtitle.individualadded"));
        }
      }
      return () => {};
    }, [
      globalValues.addIndividualAdded,
      globalValues.isIndividualReviewFinished,
    ])
  );

  const fetchData = async () => {
    try {
      const request = await sendRequest("get", "/applications", null, true);

      const individualsArray =
        request.application[0].customerDetailsCorporation.individuals;

      setIndividuals(individualsArray);

      const hasBeneficialOwner = individualsArray.some(
        (individual) =>
          individual.individualType
            .replaceAll("{", "")
            .replaceAll("}", "")
            .replaceAll('"', "") === "BENEFICIAL_OWNER"
      );

      const totalPercentageOwnership = individualsArray.reduce(
        (total, individual) => {
          return total + individual.percentageOwnership;
        },
        0
      );

      const hasAtLeastTwentyPercent = totalPercentageOwnership >= 20;

      if (hasBeneficialOwner && hasAtLeastTwentyPercent) {
        setBlocked(false);
      } else {
        toastInstance(
          "info",
          t("common:toast.subtitle.beneficialownermissing")
        );
      }
    } catch (error: any) {
      console.log(error);
    } finally {
      setRender(true);
    }
  };

  const deleteIndividual = async (individualId: string) => {
    const applicationId = storage.getString("user.auth.applicationId");
    try {
      const request = await sendRequest(
        "delete",
        "/delete-individual",
        { applicationId, individualId },
        true
      );
      if (request) {
        toastInstance("success", t("common:toast.subtitle.individualdeleted"));
      }
    } catch (error: any) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (term: string, individualId: string) => {
    if (term === "delete") {
      setLoading(true);
      deleteIndividual(individualId);
    } else if (term === "edit") {
      const individual = individuals.find(
        (individual) => individual.individualId === individualId
      );
      dispatch(
        setForm({
          individualApplicationComplete: [individual],
          isIndividualReviewing: true,
          currentCorporationIndividualId: individualId,
        })
      );
      router.replace("/individual/name");
    }
  };

  const individualCards =
    individuals.length > 0
      ? individuals.map((individual) => (
          <IndividualCard
            key={individual.individualId}
            firstName={individual.firstName}
            lastName={individual.lastName}
            individualType={individual.individualType
              .replaceAll("{", "")
              .replaceAll("}", "")
              .replaceAll('"', "")}
            handleAction={(term) => handleAction(term, individual.individualId)}
          />
        ))
      : null;

  const onSubmit = () => {
    router.push("/corporation/termsandconditions");
  };

  return (
    <ScrollView contentContainerClassName="flex-grow bg-white1">
      <View className="z-50 absolute top-0 right-0 m-8">
        <ButtonLogout />
      </View>
      {render ? (
        <>
          <View className="items-center justify-center w-full h-full">
            <Logo className="m-2" />
            <View className="m-2">
              <Title text={t("onboarding/corporation/addindividual:title")} />
            </View>
            <View className="items-center w-3/5 h-fit">
              <Subtitle
                text={t("onboarding/corporation/addindividual:subtitle")}
              />
              <View className="flex-grow items-center my-4">
                {individualCards}
                <Pressable
                  onPress={() => setModalVisible(true)}
                  onHoverIn={() => setIsHovered(true)}
                  onHoverOut={() => setIsHovered(false)}
                >
                  <View
                    className={`h-20 w-72 border rounded-md items-center justify-center ${
                      isHovered ? "border-purple5" : "border-purple1"
                    }`}
                  >
                    <AntDesign
                      name="pluscircle"
                      size={50}
                      color={isHovered ? "#9261F2" : "#5D0CBA"}
                    />
                  </View>
                </Pressable>
              </View>
              <Button
                label={t("common:button.nextstep")}
                onClick={handleSubmit(onSubmit)}
                blocked={blocked}
              />
            </View>
            <View className="flex-grow justify-end">
              <Copyright />
            </View>
          </View>
          <IndividualModal
            visible={modalVisible}
            onClose={() => setModalVisible(false)}
          />
        </>
      ) : (
        <Loader />
      )}
      {loading && <Loader />}
    </ScrollView>
  );
}
